import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';

// This component expects 'db' and 'auth' Firebase instances as props
function CustomerHistoryPanel({ db, auth, openOrderModal, handleIssueRefund, handleDeleteOrder, handleViewLabel, fetchOrders }) {
  const [userId, setUserId] = useState(null);
  const [loadingFirebase, setLoadingFirebase] = useState(true); // Renamed to avoid conflict with App.js loading
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('trackingNumber'); // Default search field
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [showResendEmailModal, setShowResendEmailModal] = useState(false); // New state for resend email modal
  const [resendEmailAddress, setResendEmailAddress] = useState(''); // New state for resend email input
  const [resendOrderData, setResendOrderData] = useState(null); // New state to hold order data for resend

  // Firebase authentication listener for this component
  useEffect(() => {
    if (!auth) {
      setMessage("Firebase Auth instance not provided to CustomerHistoryPanel.");
      setLoadingFirebase(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        console.log("CustomerHistoryPanel: Firebase Auth Ready. User ID:", user.uid);
      } else {
        console.log("CustomerHistoryPanel: No user signed in. Attempting anonymous sign-in.");
        try {
          // Use the global __initial_auth_token if available, otherwise sign in anonymously
          const initialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;
          if (initialAuthToken) {
            await signInWithCustomToken(auth, initialAuthToken);
          } else {
            await signInAnonymously(auth);
          }
        } catch (error) {
          console.error("CustomerHistoryPanel: Error during anonymous sign-in:", error);
          setMessage(`Error signing in for history panel: ${error.message}`);
        }
      }
      setLoadingFirebase(false);
    });

    return () => unsubscribe();
  }, [auth]); // Depend on 'auth' prop

  // Function to show custom modal (for internal messages, not confirmations)
  const showCustomModal = (content) => {
    setModalContent(content);
    setShowModal(true);
  };

  // Function to close custom modal
  const closeCustomModal = () => {
    setShowModal(false);
    setModalContent('');
  };

  const handleSearch = async () => {
    if (!db) {
      showCustomModal('Firebase database not initialized.');
      return;
    }
    if (!searchTerm.trim()) {
      setOrders([]);
      setMessage('Please enter a search term.');
      return;
    }

    setMessage('Searching...');
    setOrders([]); // Clear previous results

    try {
      const ordersCollectionRef = collection(db, 'orders');
      let fetchedOrders = [];
      const lowerSearchTerm = searchTerm.trim().toLowerCase(); // Convert search term to lowercase once

      // Changed 'orderId' to 'shipmentId' for consistency with the new naming
      if (searchField === 'shipmentId') {
        // If searching by shipmentId (which maps to orderId in Firestore), fetch the document directly by its ID
        // Note: Firestore document IDs are case-sensitive. If order IDs are stored in a specific case,
        // the search term should match that case.
        const docRef = doc(ordersCollectionRef, searchTerm.trim());
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          fetchedOrders.push({ id: docSnap.id, ...docSnap.data() });
        }
        console.log("Searching by Shipment ID (Order ID):", searchTerm.trim());
      } else {
        // For other fields, use a query.
        let q;

        switch (searchField) {
          case 'trackingNumber':
            // Exact match for tracking number. Assumes tracking numbers are stored consistently (e.g., all uppercase/lowercase).
            q = query(ordersCollectionRef, where('trackingNumber', '==', searchTerm.trim()));
            console.log("Searching by Tracking Number:", searchTerm.trim());
            break;
          case 'senderEmail':
            // Case-insensitive exact match for sender email by converting both query and stored data to lowercase.
            q = query(ordersCollectionRef, where('sender.email', '==', lowerSearchTerm));
            console.log("Searching by Sender Email:", lowerSearchTerm);
            break;
          case 'senderName':
            // Partial match (starts with) for sender name.
            q = query(ordersCollectionRef,
              where('sender.name', '>=', searchTerm.trim()),
              where('sender.name', '<=', searchTerm.trim() + '\uf8ff')
            );
            console.log("Searching by Sender Name (starts with):", searchTerm.trim());
            break;
          case 'senderAddress':
            // Partial match (starts with) for sender address.
            q = query(ordersCollectionRef,
              where('sender.address', '>=', searchTerm.trim()),
              where('sender.address', '<=', searchTerm.trim() + '\uf8ff')
            );
            console.log("Searching by Sender Address (starts with):", searchTerm.trim());
            break;
          case 'receiverName':
            // Partial match (starts with) for receiver name.
            q = query(ordersCollectionRef,
              where('receiver.name', '>=', searchTerm.trim()),
              where('receiver.name', '<=', searchTerm.trim() + '\uf8ff')
            );
            console.log("Searching by Receiver Name (starts with):", searchTerm.trim());
            break;
          case 'receiverAddress':
            // Partial match (starts with) for receiver address.
            q = query(ordersCollectionRef,
              where('receiver.address', '>=', searchTerm.trim()),
              where('receiver.address', '<=', searchTerm.trim() + '\uf8ff')
            );
            console.log("Searching by Receiver Address (starts with):", searchTerm.trim());
            break;
          default:
            setMessage('Invalid search field selected.');
            return;
        }

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          fetchedOrders.push({ id: doc.id, ...doc.data() });
        });
      }

      // --- Start: Process Orders and Sort ---
      const processedOrders = fetchedOrders.map(order => {
        // Convert Firestore Timestamp to Date object for sorting
        const createdAtDate = order.createdAt && typeof order.createdAt.toDate === 'function'
          ? order.createdAt.toDate()
          : (order.createdAt instanceof Date ? order.createdAt : new Date(0)); // Fallback to epoch if not a Date or Timestamp

        // Construct sender address string
        const senderAddressParts = [];
        if (order.sender?.address) senderAddressParts.push(order.sender.address);
        if (order.sender?.city) senderAddressParts.push(order.sender.city);
        if (order.sender?.province) senderAddressParts.push(order.sender.province);
        if (order.sender?.postal_code) senderAddressParts.push(order.sender.postal_code);
        const senderFullAddress = senderAddressParts.length > 0 ? senderAddressParts.join(', ') : 'N/A';

        return {
          ...order,
          createdAt: createdAtDate, // Ensure createdAt is a Date object for sorting
          senderName: order.sender?.name || 'N/A',
          senderFullAddress: senderFullAddress,
        };
      });

      // Sort orders by createdAt in descending order (newest first)
      const sortedOrders = processedOrders.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      // --- End: Process Orders and Sort ---

      setOrders(sortedOrders);
      setMessage(sortedOrders.length > 0 ? `Found ${sortedOrders.length} record(s).` : 'No records found.');

    } catch (error) {
      console.error("Error fetching orders:", error);
      setMessage(`Error searching records: ${error.message}`);
    }
  };

  // These handlers will now call the functions passed from App.js
  const handleGetShippingLabel = (order) => {
    if (handleViewLabel) {
      handleViewLabel(order);
    } else {
      showCustomModal('View Label function not available.');
    }
  };

  const handleCancelDelivery = async (order) => {
    if (handleDeleteOrder) {
      // Call the parent's handleDeleteOrder, which includes its own confirmation modal
      await handleDeleteOrder(order.id);
      fetchOrders(); // Refresh the main orders list in App.js after cancellation
      handleSearch(); // Refresh the history panel's orders
    } else {
      showCustomModal('Delete Order function not available.');
    }
  };

  const handleRefundPayment = async (order) => {
    if (handleIssueRefund) {
      // Call the parent's handleIssueRefund, which includes its own confirmation modal
      await handleIssueRefund(order);
      fetchOrders(); // Refresh the main orders list in App.js after refund
      handleSearch(); // Refresh the history panel's orders
    } else {
      showCustomModal('Issue Refund function not available.');
    }
  };

  // New function to handle resending confirmation email
  const handleResendConfirmation = (order) => {
    setResendOrderData(order);
    setResendEmailAddress(order.customerEmail || order.sender?.email || ''); // Pre-fill with customer or sender email
    setShowResendEmailModal(true);
  };

  const closeResendEmailModal = () => {
    setShowResendEmailModal(false);
    setResendEmailAddress('');
    setResendOrderData(null);
    setMessage('');
  };

  const sendResendConfirmationEmail = async () => {
    if (!resendOrderData || !resendEmailAddress.trim()) {
      setMessage('Please provide an order and a valid email address.');
      return;
    }

    setMessage('Sending confirmation email...');
    try {
      const response = await fetch('/api/resend-confirmation-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderId: resendOrderData.id,
          targetEmail: resendEmailAddress.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage(`Confirmation email sent successfully to ${resendEmailAddress.trim()}!`);
        closeResendEmailModal();
      } else {
        setMessage(`Failed to send confirmation email: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error resending confirmation email:", error);
      setMessage(`An unexpected error occurred: ${error.message}`);
    }
  };


  if (loadingFirebase) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold text-gray-700">Loading History Panel...</div>
      </div>
    );
  }

  // Display the userId for multi-user app debugging/identification
  const displayUserId = userId || 'N/A';

  return (
    <div className="bg-white p-8 rounded-lg shadow-xl border border-gray-200">
      <style>
        {`
          .modal {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: white;
            padding: 2rem;
            border-radius: 0.75rem;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            max-width: 90%;
            max-height: 90%;
            overflow-y: auto;
          }
        `}
      </style>
      <h2 className="text-4xl font-extrabold text-gray-800 mb-6 text-center">Customer History Records</h2>
      <p className="text-sm text-gray-500 text-center mb-4">History Panel User ID: <span className="font-mono text-indigo-600">{displayUserId}</span></p>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <select
          className="flex-grow p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={searchField}
          onChange={(e) => setSearchField(e.target.value)}
        >
          <option value="trackingNumber">Tracking Number</option>
          <option value="senderEmail">Sender Email</option>
          <option value="senderName">Sender Name</option>
          <option value="senderAddress">Sender Address</option>
          <option value="receiverName">Receiver Name</option>
          <option value="receiverAddress">Receiver Address</option>
        </select>
        <input
          type="text"
          placeholder={`Enter ${searchField.replace(/([A-Z])/g, ' $1').toLowerCase()}...`}
          className="flex-grow p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
        />
        <button
          onClick={handleSearch}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
        >
          Search Records
        </button>
      </div>

      {message && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded-lg relative mb-4" role="alert">
          <span className="block sm:inline">{message}</span>
        </div>
      )}

      {orders.length > 0 && (
        <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tracking No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sender</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{order.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{order.trackingNumber}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {order.senderName}
                    {order.senderFullAddress !== 'N/A' && <br />}{order.senderFullAddress}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {order.createdAt instanceof Date ? order.createdAt.toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 capitalize">{order.status}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 capitalize">{order.paymentStatus} ({order.totalCost} USD)</td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex flex-wrap gap-2">
                        {/* Issue Refund button */}
                        <button
                            onClick={() => handleRefundPayment(order)}
                            disabled={!order.paymentIntentId || order.paymentStatus === 'refunded' || order.status === 'Refunded'}
                            className={`py-1 px-3 rounded-md text-xs font-semibold transition duration-200 ${
                                (!order.paymentIntentId || order.paymentStatus === 'refunded' || order.status === 'Refunded')
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                            }`}
                        >
                            Issue Refund
                        </button>
                        {/* View Order Details button */}
                        <button
                            onClick={() => openOrderModal(order)}
                            className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded-md text-xs transition duration-200"
                        >
                            View Order
                        </button>
                        <button
                            onClick={() => handleCancelDelivery(order)}
                            disabled={order.status === 'cancelled' || order.status === 'Delivered' || order.status === 'Refunded'}
                            className={`py-1 px-3 rounded-md text-xs font-semibold transition duration-200 ${
                                order.status === 'cancelled' || order.status === 'Delivered' || order.status === 'Refunded'
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-red-500 hover:bg-red-600 text-white'
                            }`}
                        >
                            Delete
                        </button>
                        <button
                            onClick={() => handleGetShippingLabel(order)}
                            disabled={!order.labelUrl}
                            className={`py-1 px-3 rounded-md text-xs font-semibold transition duration-200 ${
                                order.labelUrl
                                    ? 'bg-green-500 hover:bg-green-600 text-white'
                                    : 'bg-gray-400 cursor-not-allowed'
                            }`}
                        >
                            View Label
                        </button>
                        {/* New Resend Confirmation button */}
                        <button
                            onClick={() => handleResendConfirmation(order)}
                            disabled={!order.trackingNumber || !order.labelUrl || !order.totalCost} // Disable if critical info is missing
                            className={`py-1 px-3 rounded-md text-xs font-semibold transition duration-200 ${
                                (!order.trackingNumber || !order.labelUrl || !order.totalCost)
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                            }`}
                        >
                            Resend Confirmation
                        </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Custom Modal (for general messages) */}
      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <h2 className="text-xl font-bold mb-4">Notification</h2>
            <p className="text-gray-700 mb-6">{modalContent}</p>
            <button
              onClick={closeCustomModal}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Resend Email Modal */}
      {showResendEmailModal && resendOrderData && (
        <div className="modal">
          <div className="modal-content">
            <h2 className="text-xl font-bold mb-4">Resend Order Confirmation</h2>
            <p className="text-gray-700 mb-4">Order ID: <span className="font-semibold">{resendOrderData.id}</span></p>
            <p className="text-gray-700 mb-4">Tracking No: <span className="font-semibold">{resendOrderData.trackingNumber}</span></p>
            <div className="mb-4">
              <label htmlFor="resendEmail" className="block text-gray-700 text-sm font-bold mb-2">
                Recipient Email Address:
              </label>
              <input
                type="email"
                id="resendEmail"
                className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={resendEmailAddress}
                onChange={(e) => setResendEmailAddress(e.target.value)}
                placeholder="Enter email address"
                required
              />
            </div>
            {message && (
                <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded-lg relative mb-4" role="alert">
                    <span className="block sm:inline">{message}</span>
                </div>
            )}
            <div className="flex justify-end space-x-4">
              <button
                onClick={closeResendEmailModal}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg shadow-md transition duration-300"
              >
                Cancel
              </button>
              <button
                onClick={sendResendConfirmationEmail}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300"
              >
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerHistoryPanel;
